import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { EditOutlined, PlusOutlined, SearchOutlined, FilterFilled } from '@ant-design/icons';
import { Table, Tooltip, Icon } from 'antd';
import CreateAccountModal from '~pages/dashboard/account/create-account-modal';
import EditAccountModal from '~pages/dashboard/account/edit-account-modal';
import * as accountActions from '~actions/account';
import {
  communicationStatus,
  sortDirectionMapping,
  accountSortByMapping,
  platformEntity,
  entityAction,
} from '~constants';
import showElementByAuth, { isSupplyRole, isRegionalSupplyHeadRole } from '~helpers/auth';
import { dayFormatType, formatDay } from '~helpers/day';
import { TableSearchInput } from 'common/components/table-filter-component';
import classNames from 'classnames';

const removeSpace = text => {
  if (text) {
    return text.trim() || null;
  }
  return null;
};

const mapStateToProps = state => {
  const data = state.dashboard.account?.toJS();

  return {
    listStatus: data.communication.list.status,
    users: data.list,
  };
};

const mapDispatchToProps = dispatch => ({
  getUserList: (params, onSuccess) => {
    dispatch(accountActions.getUserList(params, onSuccess));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
export default class InternalManagement extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showCreateModal: false,
      showEditModal: false,
      editData: {},
      searchComponentTarget: null,
      sortDirection: sortDirectionMapping.descend,
      tableHeight: 0,
      pagination: {
        current: 1,
        pageSize: 10,
      },
      queryFilter: {
        searchAccountName: null,
        searchAccountEmail: null,
        enabled: null,
      },
      sortBy: accountSortByMapping.updatedAt,
      showSearchAccountNameFilter: false,
      showSearchAccountEmailFilter: false,
    };
  }

  columns = () => [
    {
      title: this.props.t('cms.account.table_title.account_name'),
      dataIndex: 'fullName',
      key: 'name',
      width: 160,
      render: text => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>,
      filterDropdown: (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            field="searchAccountName"
            value={this.state.queryFilter.searchAccountName || ''}
            onChange={(_fieldName, value) => {
              this.setState(
                {
                  queryFilter: {
                    ...this.state.queryFilter,
                    searchAccountName: removeSpace(value),
                  },
                  pagination: {
                    ...this.state.pagination,
                    current: 1,
                  },
                  showSearchAccountNameFilter: false,
                },
                this.getList,
              );
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined
          style={{ color: this.state.queryFilter.searchAccountName ? '#38b2a6' : '#c8c9cb' }}
        />
      ),
      filterDropdownVisible: this.state.showSearchAccountNameFilter,
      onFilterDropdownVisibleChange: view => {
        this.setState({
          showSearchAccountNameFilter: view,
        });
      },
    },
    {
      title: this.props.t('cms.account.table_title.account_email'),
      dataIndex: 'email',
      key: 'email',
      width: 260,
      render: text => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>,
      filterDropdown: (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            field="searchAccountEmail"
            value={this.state.queryFilter.searchAccountEmail || ''}
            onChange={(_fieldName, value) => {
              this.setState(
                {
                  queryFilter: {
                    ...this.state.queryFilter,
                    searchAccountEmail: removeSpace(value),
                  },
                  pagination: {
                    ...this.state.pagination,
                    current: 1,
                  },
                  showSearchAccountEmailFilter: false,
                },
                this.getList,
              );
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined
          style={{ color: this.state.queryFilter.searchAccountEmail ? '#38b2a6' : '#c8c9cb' }}
        />
      ),
      filterDropdownVisible: this.state.showSearchAccountEmailFilter,
      onFilterDropdownVisibleChange: view => {
        this.setState({
          showSearchAccountEmailFilter: view,
        });
      },
    },
    {
      title: this.props.t('cms.account.table_title.user_role'),
      dataIndex: 'userRoles',
      key: 'userRoles',
      width: 140,
      render: text => (
        <Choose>
          <When condition={text && text.length > 0}>
            <Tooltip
              title={
                <div
                  style={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    maxWidth: 140,
                  }}
                >
                  <For of={text} each="item" index="index">
                    <span key={item.slug}>
                      {index + 1 !== text.length ? `${item.name},` : item.name}
                    </span>
                  </For>
                </div>
              }
            >
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 140,
                }}
              >
                <For of={text} each="item" index="index">
                  <span key={item.slug}>
                    {index + 1 !== text.length ? `${item.name},` : item.name}
                  </span>
                </For>
              </div>
            </Tooltip>
          </When>
          <Otherwise>
            <span> - </span>
          </Otherwise>
        </Choose>
      ),
    },
    {
      title: this.props.t('cms.account.table_title.updated_time'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      sorter: true,
      render: text => (
        <Choose>
          <When condition={text}>
            <span>{formatDay(dayFormatType.UKFORMAT, text)}</span>
          </When>
          <Otherwise>
            <span> - </span>
          </Otherwise>
        </Choose>
      ),
    },
    {
      title: this.props.t('cms.account.table_title.last_activity'),
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      sorter: true,
      width: 200,
      render: text => (
        <Choose>
          <When condition={text}>
            <span>{formatDay(dayFormatType.UKWITHHMS, text)}</span>
          </When>
          <Otherwise>
            <span> - </span>
          </Otherwise>
        </Choose>
      ),
    },
    {
      title: this.props.t('cms.account.table_title.enabled'),
      dataIndex: 'enabled',
      key: 'isEnabled',
      width: 150,
      filters: [
        { text: 'Enabled', value: 'true' },
        { text: 'Disabled', value: 'false' },
      ],
      filteredValue:
        typeof this.state.queryFilter.enabled === 'boolean'
          ? [String(this.state.queryFilter.enabled)]
          : null,
      filterMultiple: false,
      filterIcon: (
        <FilterFilled
          style={{
            color: typeof this.state.queryFilter.enabled === 'boolean' ? '#38b2a6' : '#c8c9cb',
          }}
        />
      ),
      render: (text, record) => {
        return (
          <span>
            <Icon
              className={classNames('internal-account-management__user-enabled-icon', {
                'internal-account-management__user-enabled-icon--enabled': record.enabled,
                'internal-account-management__user-enabled-icon--disabled': !record.enabled,
              })}
              type={record.enabled ? 'check' : 'close'}
            />
            {record.enabled ? 'Enabled' : 'Disabled'}
          </span>
        );
      },
    },
    {
      title: this.props.t('cms.account.table_title.action'),
      key: 'action',
      width: 80,
      render: (text, record) => (
        <If condition={showElementByAuth(platformEntity.IDENTITY_CMS_USERS, entityAction.UPDATE)}>
          <button
            onClick={() => {
              this.handleEditRecord(record);
            }}
            className="landlord-account-management__btn"
          >
            <EditOutlined />
          </button>
        </If>
      ),
    },
  ];

  componentDidMount() {
    this.getLandlordTableHeight();
    this.props.getUserList({
      pageSize: this.state.pagination.pageSize,
      pageNumber: this.state.pagination.current,
    });
    window.addEventListener('resize', this.getLandlordTableHeight);
  }

  getList = () => {
    this.props.getUserList({
      sortBy: this.state.sortBy,
      sortDirection: this.state.sortDirection,
      pageSize: this.state.pagination.pageSize,
      pageNumber: this.state.pagination.current,
      searchName: this.state.queryFilter.searchAccountName,
      email: this.state.queryFilter.searchAccountEmail,
      enabled: this.state.queryFilter.enabled,
    });
  };

  componentWillUnMount() {
    window.removeEventListener('resize', this.getLandlordTableHeight);
  }

  handleEditRecord = record => {
    this.setState({
      showEditModal: true,
      editData: record,
    });
  };

  clickCreateModal = () => {
    this.setState({
      showCreateModal: !this.state.showCreateModal,
    });
  };

  clickEditModal = () => {
    this.setState({
      showEditModal: !this.state.showEditModal,
    });
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({
      sortDirection: sortDirectionMapping[sorter.order],
      sortBy: accountSortByMapping[sorter.columnKey],
      queryFilter: {
        ...this.state.queryFilter,
        enabled: filters.isEnabled?.length ? filters.isEnabled[0] === 'true' || false : null,
      },
      pagination: {
        pageSize: pagination.pageSize,
        current: pagination.current,
        total: pagination.total,
      },
    });

    this.props.getUserList({
      sortBy: accountSortByMapping[sorter.columnKey],
      sortDirection: sortDirectionMapping[sorter.order],
      enabled: filters.isEnabled?.length ? filters.isEnabled[0] === 'true' || false : null,
      pageSize: pagination.pageSize,
      pageNumber: pagination.current,
      searchName: this.state.queryFilter.searchAccountName,
      email: this.state.queryFilter.searchAccountEmail,
    });
  };

  getLandlordTableHeight = () => {
    if (this.landlordManagement) {
      this.setState({ tableHeight: this.landlordManagement.clientHeight - 120 });
    }
  };

  render() {
    const { t } = this.props;
    return (
      <div
        className="internal-account-management"
        ref={node => {
          this.landlordManagement = node;
        }}
      >
        <div className="landlord-account-management__header">
          <If
            condition={
              showElementByAuth(platformEntity.IDENTITY_CMS_USERS, entityAction.CREATE) &&
              !isSupplyRole() &&
              !isRegionalSupplyHeadRole()
            }
          >
            <button
              className="landlord-account-management__btn
              landlord-account-management__create-new-account"
              type="button"
              id="create_cms_account_button"
              onClick={this.clickCreateModal}
            >
              <PlusOutlined style={{ fontSize: '12px', marginLeft: '4px' }} />
              {t('cms.account.landlord_account_managing.create_new.account.btn')}
            </button>
          </If>
        </div>

        <div className="internal-account-management__content">
          <Table
            rowKey={record => record.id}
            dataSource={this.props.users.payload}
            columns={this.columns()}
            pagination={{
              total: this.props.users.total,
              current: this.state.pagination.current,
              pageSize: this.state.pagination.pageSize,
              showSizeChanger: true,
            }}
            onChange={this.handleTableChange}
            loading={this.props.listStatus !== communicationStatus.IDLE}
            scroll={{ x: true }}
          />
        </div>
        <If condition={this.state.showCreateModal}>
          <CreateAccountModal
            activeModal
            t={t}
            onClose={this.clickCreateModal}
            modalType="add"
            getList={this.getList}
          />
        </If>
        <If condition={this.state.showEditModal}>
          <div className="landlord-account-management__edit-modal">
            <EditAccountModal
              activeModal
              t={t}
              onClose={this.clickEditModal}
              modalType="edit"
              data={this.state.editData}
              history={this.props.history}
              getList={this.getList}
            />
          </div>
        </If>
      </div>
    );
  }
}

InternalManagement.propTypes = {
  t: PropTypes.func,
  getUserList: PropTypes.func,
  listStatus: PropTypes.string,
  users: PropTypes.object,
  history: PropTypes.object,
};

InternalManagement.defaultProps = {
  t: () => {},
  getUserList: () => {},
  listStatus: '',
  users: {},
  history: {},
};
